// =============================================================================
// Document Management — the org's own folder tree, versions and permissions.
//
//   DriveFolder ──< DriveFolder (any depth)
//        └──< DriveFile ──< DriveFileVersion (immutable chain, newest is `current`)
//
// Named `drive` rather than `document` on purpose: three other things here are already called documents
// (projectDocuments, projectManagementDocuments and its versions), and a reader of `DriveFile` should
// never have to work out which store it is. Everything a user reads says "Documents".
//
// Two deliberate denormalizations, both rewritten by the same write that moves a node:
//   - `ancestorIds` (root→parent) so "everything under X" is one indexed selector, which is what makes
//     repermissioning and subtree listing possible at all.
//   - `path` ('/Legal/Contracts/2026') for breadcrumbs, prefix search and zip entry names.
//
// Permissions are AUTHORED sparsely and MATERIALIZED densely. A folder or file carries an `acl` only
// where someone set one; `readers`/`writers`/`managers` are then stamped onto every descendant, because
// "the nearest ancestor that defines an ACL" cannot be expressed in a Mongo selector and every listing
// has to be a single indexed query. driveMaterializeSubtree is idempotent, so a repair pass is always
// safe to run.
//
// Org scope follows the sentinel convention (orgQuery in server/methods/register.ts), NOT inventory's:
// a caller with no org sees nothing rather than every org's documents. A document store is the wrong
// place for an accidentally cross-tenant read.
// =============================================================================

import { Mongo } from 'meteor/mongo';
import { Meteor } from 'meteor/meteor';
import { Doc, FileMeta } from '/imports/types';
import { DeclareMethod } from '/imports/methods';
import { Roles } from '/imports/api/accounts';

type UserLike = { _id?: string, isSuperUser?: boolean } | null | undefined;
type AccountLike = { roles?: string[] } | null | undefined;

/**
 * Who may open the module at all. Its own grant: holding project management says nothing about whether
 * someone should read the org's legal, finance or HR folders.
 * @param user - the viewing user
 * @param account - that user's account in the current org
 * @returns true when the Documents module is visible
 */
export const driveCanAccess = (user?: UserLike, account?: AccountLike): boolean =>
  !!user?.isSuperUser || !!account?.roles?.includes(Roles.documents);

/**
 * Who sees the whole tree regardless of per-folder permissions, may create root folders, and may set any
 * node's permissions. The paired-role pattern: the grant alone opens the module, admin alongside it is
 * what makes someone the drive's administrator.
 * @param user - the acting user
 * @param account - that user's account in the current org
 * @returns true when the caller administers the drive org-wide
 */
export const driveCanManageOrg = (user?: UserLike, account?: AccountLike): boolean =>
  !!user?.isSuperUser || (!!account?.roles?.includes(Roles.documents) && !!account?.roles?.includes(Roles.admin));

// --- Permissions ------------------------------------------------------------------------------------

export const driveAccessLevels = ['viewer', 'editor', 'manager'] as const;
export type DriveAccessLevel = typeof driveAccessLevels[number];

/** Rank, so a principal matching several entries keeps the most generous one. */
export const driveAccessRank: { [K in DriveAccessLevel]: number } = { viewer: 1, editor: 2, manager: 3 };

/**
 * One grant. `principal` is a prefixed key rather than a tagged union so that materializing it is a plain
 * array of strings a single `$in` can match:
 *   'u:<userId>'  one person
 *   'r:<role>'    everyone whose account holds that role in this org
 *   'everyone'    everyone who can open the module in this org
 */
export interface DriveAclEntry {
  principal: string;
  access: DriveAccessLevel;
}

export const drivePrincipalEveryone = 'everyone';
/** @param userId - the user @returns that user's principal key */
export const drivePrincipalUser = (userId: string) => `u:${userId}`;
/** @param role - an account role @returns that role's principal key */
export const drivePrincipalRole = (role: string) => `r:${role}`;

/**
 * Reads a principal key back into its parts, for rendering a permissions list.
 * @param principal - the key
 * @returns its kind and, for a user or role, the id
 */
export const drivePrincipalParts = (principal: string): { kind: 'user' | 'role' | 'everyone', id?: string } => {
  if (principal === drivePrincipalEveryone) return { kind: 'everyone' };
  if (principal.startsWith('u:')) return { kind: 'user', id: principal.slice(2) };
  if (principal.startsWith('r:')) return { kind: 'role', id: principal.slice(2) };
  return { kind: 'everyone' };
};

/**
 * Every principal key a caller presents. Order is irrelevant; the server matches with `$in`.
 * @param user - the caller
 * @param account - that caller's account in the current org
 * @returns the caller's principal keys
 */
export const drivePrincipalKeys = (user?: UserLike, account?: AccountLike): string[] => [
  drivePrincipalEveryone,
  ...(user?._id ? [drivePrincipalUser(user._id)] : []),
  ...(account?.roles || []).map(drivePrincipalRole),
];

/**
 * The ACL in force for the last node of a chain: the nearest definition walking from the node back to the
 * root. An inherited node carries no `acl` of its own, so inheritance is the absence of a decision rather
 * than a copy of one — which is what lets a parent's change reach everything beneath it.
 * @param chain - the nodes from root to the node in question, inclusive
 * @returns the governing entries, empty when nothing in the chain defines any (which grants nobody)
 */
export const driveEffectiveAcl = (chain: { acl?: DriveAclEntry[] }[]): DriveAclEntry[] => {
  for (let i = chain.length - 1; i >= 0; i--) {
    const acl = chain[i]?.acl;
    if (acl?.length) return acl;
  }
  return [];
};

/**
 * Flattens entries into the three materialized arrays. A manager can do everything an editor can and an
 * editor everything a viewer can, so the arrays nest rather than partition.
 * @param acl - the governing entries
 * @returns the principal keys to stamp on the node
 */
export const driveAclPrincipals = (acl: DriveAclEntry[]): { readers: string[], writers: string[], managers: string[] } => {
  const readers = new Set<string>();
  const writers = new Set<string>();
  const managers = new Set<string>();
  for (const entry of acl) {
    if (!entry?.principal) continue;
    readers.add(entry.principal);
    if (entry.access === 'editor' || entry.access === 'manager') writers.add(entry.principal);
    if (entry.access === 'manager') managers.add(entry.principal);
  }
  return { readers: [...readers], writers: [...writers], managers: [...managers] };
};

/**
 * The caller's level on a node whose materialized arrays are already loaded. Pure, so the client can grey
 * out a control with the same answer the server will give when the control is used anyway.
 * @param node - a folder or file carrying its materialized principal arrays
 * @param keys - the caller's principal keys
 * @param orgManager - whether the caller administers the drive org-wide
 * @returns the level, or undefined when the node is not visible to this caller
 */
export const driveAccessFor = (
  node: { readers?: string[], writers?: string[], managers?: string[] } | undefined | null,
  keys: string[],
  orgManager?: boolean,
): DriveAccessLevel | undefined => {
  if (orgManager) return 'manager';
  if (!node) return undefined;
  const holds = (list?: string[]) => !!list?.some((principal) => keys.includes(principal));
  if (holds(node.managers)) return 'manager';
  if (holds(node.writers)) return 'editor';
  if (holds(node.readers)) return 'viewer';
  return undefined;
};

/**
 * Whether a level is at least the one required.
 * @param access - the level held, if any
 * @param required - the level needed
 * @returns true when the held level suffices
 */
export const driveAccessAtLeast = (access: DriveAccessLevel | undefined, required: DriveAccessLevel): boolean =>
  !!access && driveAccessRank[access] >= driveAccessRank[required];

/** What a new root folder grants when its creator says nothing: the whole module, read and write. */
export const driveDefaultRootAcl: DriveAclEntry[] = [{ principal: drivePrincipalEveryone, access: 'editor' }];

// --- Stored blobs -----------------------------------------------------------------------------------

export const driveStorageKinds = ['nfs', 's3'] as const;
export type DriveStorageKind = typeof driveStorageKinds[number];

/**
 * Where a version's bytes actually are. Deliberately not a FileMeta: a FileMeta carries a permanent
 * never-expiring token, and nothing in this module hands one of those to a browser.
 */
export interface DriveStoredBlob {
  storage: DriveStorageKind;
  url: string;        // '/nfs/drive/<slug>/<hash>.<ext>' for nfs, the object key's canonical url for s3
  key?: string;       // the driver's own handle, when url alone isn't enough to address the object
  size: number;
  md5: string;
  type?: string;
}

/**
 * A derived copy of a file, made so that a browser can show something it could not otherwise render.
 * Both are rebuilt from the current version, so a new version clears them.
 */
export interface DriveRendition {
  pdf?: DriveStoredBlob;       // an Office document converted, because no browser renders .docx
  thumb?: DriveStoredBlob;     // a small image: the first page, or the picture scaled down
  at: Date;
  status?: 'pending' | 'done' | 'failed';
}

// What each format gets. Sheets are NOT in the pdf set: a spreadsheet read as a table is better than a
// spreadsheet read as a picture of a page, and converting one costs a soffice run for a worse answer.
export const drivePdfRenditionExtensions = ['docx', 'doc', 'odt', 'rtf', 'pptx', 'ppt', 'odp'];
export const driveSheetExtensions = ['xlsx', 'xls', 'ods', 'csv'];
export const driveEditableExtensions = ['txt', 'md', 'markdown', 'csv', 'json', 'log', 'xml', 'yml', 'yaml'];
export const driveThumbExtensions = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'tif', 'tiff', 'bmp'];

// Archives this module can look inside. Only zip, and the reason is random access: a zip carries a
// central directory, so one photograph is read without touching the rest of the file. A .tar.gz is a
// single gzip stream, so every view would re-inflate the archive from the beginning — the same gallery
// on a 2 GB tarball would be unusable. 7z and rar would each need a dependency the image does not have.
export const driveArchiveExtensions = ['zip'];
// What a gallery shows. The thumbnailable image formats; a PDF inside an archive is listed among the
// 'other' files rather than drawn, because a gallery of mixed documents reads as neither.
export const driveGalleryExtensions = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'tif', 'tiff', 'bmp', 'heic'];

/** @param ext - a file's extension @returns whether this module can show what is inside it */
export const driveIsArchive = (ext?: string) => driveArchiveExtensions.includes((ext || '').toLowerCase());
/** @param name - a name inside an archive @returns whether it belongs in the gallery rather than the leftovers */
export const driveIsGalleryImage = (name: string) => driveGalleryExtensions.includes((driveExtOf(name) || '').toLowerCase());

/** @param ext - a file's extension @returns whether this module can show it as a table */
export const driveIsSheet = (ext?: string) => driveSheetExtensions.includes((ext || '').toLowerCase());
/** @param ext - a file's extension @returns whether this module can edit it in place */
export const driveIsEditable = (ext?: string) => driveEditableExtensions.includes((ext || '').toLowerCase());

// --- Collections ------------------------------------------------------------------------------------

export interface DriveFolder extends Doc {
  orgId?: string;
  parentId?: string;         // absent = a root folder
  ancestorIds: string[];     // root→parent, ordered
  path: string;              // '/Legal/Contracts/2026'
  name: string;
  kind?: 'system';           // auto-created for another record; not renamable or deletable by hand
  ref?: { collection: string, id: string };
  acl?: DriveAclEntry[];     // absent = inherited
  readers: string[];
  writers: string[];
  managers: string[];
  trashedAt?: Date;
  trashedByUserId?: string;
  // Set on everything BELOW the folder that was trashed, and never on that folder itself. So the trash
  // view is "trashed and not trashed as part of something else", and restoring reverts exactly the set
  // one action buried.
  trashedRootId?: string;
}

export interface DriveFile extends Doc {
  orgId?: string;
  folderId: string;
  ancestorIds: string[];     // the folder's chain plus the folder itself, so one bulk op covers a subtree
  name: string;
  ext?: string;
  currentVersionId?: string;
  size?: number;             // of the current version, so a listing needs no join
  type?: string;
  md5?: string;
  versionCount?: number;
  acl?: DriveAclEntry[];
  readers: string[];
  writers: string[];
  managers: string[];
  // The record this file belongs to, when another module owns it — a Project Management document, say.
  // Such a file is filed and versioned from there, so the drive will not let it be trashed or deleted
  // here: the two views would then disagree about whether the document exists.
  ref?: { collection: string, id: string };
  // Where this file came from, when it was not uploaded by a person — the SharePoint path an import
  // read it from, say. Provenance, and the key that stops a re-run filing it twice.
  source?: { kind: string, ref: string };
  starredBy?: string[];
  trashedAt?: Date;
  trashedByUserId?: string;
  trashedRootId?: string;
  rendition?: DriveRendition;
  // Where content extraction got to. 'skipped' is a photo or a format nothing can read — which is not a
  // failure, and is what the offer to run OCR hangs off.
  textStatus?: 'pending' | 'done' | 'failed' | 'skipped';
  // What looking inside an archive found. Present only on an archive, and `others` is how the gallery
  // can say "and 3 files that are not pictures" rather than silently dropping them.
  gallery?: {
    status: 'pending' | 'done' | 'failed';
    count?: number;
    others?: number;
    thumbWidth?: number;     // what the thumbnails were made at, so a later re-index knows it is owed one
    at: Date;
    error?: string;
  };
}

export interface DriveFileVersion extends Doc {
  orgId?: string;
  driveFileId: string;
  versionNumber: number;
  blob: DriveStoredBlob;
  name: string;              // the filename as uploaded for this version
  size: number;
  md5: string;
  type?: string;
  changeNote?: string;
  status: 'current' | 'superseded';
}

/**
 * A link that works without a login. The ROW is the authority, never the token: the token carries nothing
 * but which share it is, so expiry can be changed and access withdrawn after the link has gone out. That
 * is the whole reason this is a collection and not another signed url.
 */
export interface DriveShare extends Doc {
  orgId?: string;
  target: { kind: 'folder' | 'file', id: string };
  label?: string;
  expiresAt?: Date;
  // sha256 of a code delivered out of band, as the solar-bill links do it. Never the code itself.
  codeHash?: string;
  requireEmail?: boolean;        // ask who is visiting; recorded, NOT verified
  revokedAt?: Date;
  revokedByUserId?: string;
  visitCount?: number;
  lastVisitAt?: Date;
  // A wrong code is cheap to retry over DDP, so a run of them stops the share for a while.
  failedCount?: number;
  lockedUntil?: Date;
}

/** One opening of a share. The only record of who read a document that was sent outside. */
export interface DriveShareVisit extends Doc {
  orgId?: string;
  shareId: string;
  at: Date;
  ipAddr?: string;
  userAgent?: string;
  email?: string;                // what the visitor typed, when the share asks
  driveFileId?: string;          // the file they opened, when they opened one
}

/**
 * Extracted plain text per version, for content search. Server-only, NEVER published: a scanned contract
 * runs to hundreds of KB and no client needs it — the search method returns a snippet instead.
 */
export interface DriveFileText {
  _id?: string;
  orgId?: string;
  driveFileId: string;
  versionId: string;
  text: string;
  extractedAt: Date;
  ocr?: boolean;                 // whether the text came from OCR rather than an embedded text layer
}

/** Append-only. Who did what to which node, and the only account of it. */
export interface DriveEvent extends Doc {
  orgId?: string;
  kind: 'folder' | 'file' | 'version' | 'permissions' | 'share';
  action: string;
  ref: { collection: string, id: string };
  label?: string;
  at: Date;
  byUserId?: string;
  byName?: string;
  meta?: { [key: string]: string | number | boolean | Date | null };
}

/**
 * One file inside an archive, indexed so a gallery can be drawn without unpacking anything.
 *
 * Keyed on the ARCHIVE'S CONTENT (`archiveMd5`), not on a drive row: the same zip reached through a
 * Project Management document and through /documents is one index, and a zip whose bytes never made it
 * into the drive can still be read. The original is never copied out — only `thumb` is stored, and a
 * full-size view streams the entry out of the archive where it already is.
 */
export interface DriveArchiveEntry extends Doc {
  orgId?: string;
  archiveMd5: string;
  archiveUrl: string;        // where the bytes were when this was indexed; the stream reads from here
  path: string;              // the entry's own path inside the archive, exactly as stored
  name: string;              // its basename, which is what the gallery captions
  ext?: string;
  size: number;              // uncompressed
  index: number;             // the archive's own order, so a gallery reads as the camera filed it
  thumb?: DriveStoredBlob;
  driveFileId?: string;      // denormalized for the orphan sweep and for erase; absent when unadopted
  driveVersionId?: string;
}

export const DriveFolders = new Mongo.Collection<DriveFolder>('driveFolders');
export const DriveFiles = new Mongo.Collection<DriveFile>('driveFiles');
export const DriveFileVersions = new Mongo.Collection<DriveFileVersion>('driveFileVersions');
export const DriveEvents = new Mongo.Collection<DriveEvent>('driveEvents');
export const DriveShares = new Mongo.Collection<DriveShare>('driveShares');
export const DriveShareVisits = new Mongo.Collection<DriveShareVisit>('driveShareVisits');
export const DriveFileTexts = new Mongo.Collection<DriveFileText>('driveFileTexts');
export const DriveArchiveEntries = new Mongo.Collection<DriveArchiveEntry>('driveArchiveEntries');

/** How long a share's own code stays locked out after a run of wrong ones. */
export const driveShareLockMinutes = 15;
/** Wrong codes tolerated before that lock. */
export const driveShareMaxFailures = 10;

/**
 * Whether a share is usable right now. Pure, so the owner's list and the public reader answer alike.
 * @param share - the share row
 * @param now - the moment to judge it at
 * @returns why it is unusable, or undefined when it is fine
 */
export const driveShareUnusable = (share: DriveShare, now = new Date()): 'revoked' | 'expired' | 'locked' | undefined => {
  if (share.revokedAt) return 'revoked';
  if (share.expiresAt && new Date(share.expiresAt).valueOf() <= now.valueOf()) return 'expired';
  if (share.lockedUntil && new Date(share.lockedUntil).valueOf() > now.valueOf()) return 'locked';
  return undefined;
};

// --- Names and paths --------------------------------------------------------------------------------

/** Separator, and what a name may therefore not contain. */
export const drivePathSeparator = '/';

/**
 * Cleans a folder or file name. The separator and control characters go because `path` is built by
 * joining on the separator, and a name carrying one would make the stored path unreadable back.
 * @param name - the name as typed
 * @returns a usable name, empty when nothing survives
 */
export const driveCleanName = (name?: string): string => {
  if (typeof name !== 'string') return '';
  // eslint-disable-next-line no-control-regex
  return name.replace(/[\u0000-\u001f\u007f/\\]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 255);
};

/**
 * Joins a parent path and a name.
 * @param parentPath - the parent's path, or undefined at the root
 * @param name - the cleaned name
 * @returns the node's path
 */
export const drivePathFor = (parentPath: string | undefined, name: string): string =>
  `${parentPath && parentPath !== drivePathSeparator ? parentPath : ''}${drivePathSeparator}${name}`;

/**
 * The extension of a filename, lowercased, without the dot.
 * @param name - the filename
 * @returns the extension, or undefined when there isn't one
 */
export const driveExtOf = (name?: string): string | undefined => {
  const parts = (name || '').split('.');
  if (parts.length < 2) return undefined;
  const ext = parts[parts.length - 1].toLowerCase();
  return /^[a-z0-9]{1,12}$/.test(ext) ? ext : undefined;
};

// --- Reads the UI makes ------------------------------------------------------------------------------

/** One ancestor on the way to the open folder. */
export interface DriveCrumb {
  _id: string;
  name: string;
}

export interface DriveFolderRow {
  _id: string;
  name: string;
  parentId?: string;
  path: string;
  kind?: 'system';
  inherited: boolean;        // whether it carries its own acl, for the padlock in the tree
  access: DriveAccessLevel;
  childFolders: number;
  files: number;
}

export interface DriveFileRow {
  _id: string;
  name: string;
  ext?: string;
  size?: number;
  type?: string;
  versionCount?: number;
  currentVersionId?: string;
  updatedAt?: Date;
  updatedByName?: string;
  starred: boolean;
  inherited: boolean;
  access: DriveAccessLevel;
  managedBy?: string;        // the module that owns this file, when one does
  textStatus?: 'pending' | 'done' | 'failed' | 'skipped';
  // A minted, expiring url for the thumbnail, when one has been made. Sent with the row because a list
  // of photographs is unreadable without them and a round-trip per row would not be.
  thumbUrl?: string;
  hasPdfRendition?: boolean;
  renditionPending?: boolean;
}

export interface DriveListing {
  folderId?: string;
  crumbs: DriveCrumb[];
  access?: DriveAccessLevel;     // on the open folder; undefined at a root listing
  canCreateFolder: boolean;
  folders: DriveFolderRow[];
  files: DriveFileRow[];
  total: number;                 // files matching, before paging
}

export interface DriveVersionRow {
  _id: string;
  versionNumber: number;
  name: string;
  size: number;
  type?: string;
  changeNote?: string;
  status: 'current' | 'superseded';
  createdAt?: Date;
  createdByName?: string;
}

export interface DriveEventRow {
  _id: string;
  kind: DriveEvent['kind'];
  action: string;
  label?: string;
  at: Date;
  byName?: string;
  meta?: DriveEvent['meta'];
}

/** One grantable principal, as the permissions picker offers it. */
export interface DrivePrincipalOption {
  principal: string;
  label: string;
  kind: 'user' | 'role' | 'everyone';
}

export interface DriveFileDetail {
  file: DriveFileRow;
  folderId: string;
  crumbs: DriveCrumb[];
  acl: DriveAclEntry[];          // the entries in force, whether inherited or this node's own
  ownAcl?: DriveAclEntry[];      // set only when the file overrides its folder
  versions: DriveVersionRow[];
  events: DriveEventRow[];
  duplicates?: { _id: string, name: string, path: string }[];   // same md5 elsewhere in the org
}

export interface DriveShareRow {
  _id: string;
  label?: string;
  target: { kind: 'folder' | 'file', id: string };
  targetName: string;
  url: string;
  expiresAt?: Date;
  hasCode: boolean;
  requireEmail: boolean;
  revokedAt?: Date;
  state: 'live' | 'revoked' | 'expired' | 'locked';
  visitCount: number;
  lastVisitAt?: Date;
  createdAt?: Date;
  createdByName?: string;
}

export interface DriveShareVisitRow {
  _id: string;
  at: Date;
  ipAddr?: string;
  email?: string;
  fileName?: string;
}

/** What a visitor with no login is shown. Carries minted, expiring urls — never a stored file token. */
export interface DriveSharePayload {
  label?: string;
  kind: 'folder' | 'file';
  rootName: string;
  crumbs: DriveCrumb[];                 // within the share only; the folders above it are not named
  folders: { _id: string, name: string }[];
  files: { _id: string, name: string, ext?: string, size?: number, type?: string, url: string }[];
}

/** What the reader asks for before it will show anything. */
export interface DriveShareChallenge {
  needsCode?: boolean;
  needsEmail?: boolean;
  wrong?: boolean;              // the code just tried was not the one
  label?: string;
}

export interface DriveSheetPreview {
  sheets: string[];
  sheet: string;
  rows: (string | number | boolean | null)[][];
  truncated: boolean;
}

export interface DriveTextContent {
  text: string;
  truncated: boolean;      // too big to hold in a browser; shown read-only rather than silently clipped
  editable: boolean;
}

export interface DriveZipResult {
  url: string;
  name: string;
  files: number;
  skipped: number;
}

/** One tile in a gallery. Both urls are minted and short-lived, like everything else this module hands out. */
export interface DriveGalleryItem {
  index: number;
  name: string;
  size: number;
  thumbUrl?: string;      // absent while the index is still running, or when this one could not be drawn
  url: string;            // the original, streamed out of the archive
}

export interface DriveGalleryPage {
  status: 'pending' | 'done' | 'failed' | 'unsupported';
  total: number;          // pictures in the archive
  others: number;         // everything else in it, which the gallery names but cannot show
  items: DriveGalleryItem[];
  name: string;           // the archive's own name, for the modal title
  archiveUrl?: string;    // the whole zip, for "download the archive"
}

export interface DriveSearchHit extends DriveFileRow {
  path: string;
  snippet?: string;                     // the matching line of the document's text, when the match was in it
}

export interface DriveSearchResult {
  folders: DriveFolderRow[];
  files: DriveSearchHit[];
  truncated: boolean;
}

// --- Methods ----------------------------------------------------------------------------------------

export const driveListFolder = DeclareMethod<
  { folderId?: string, limit?: number, skip?: number, trashed?: boolean, starred?: boolean },
  DriveListing
>('driveListFolder');
/** Names and contents, across everything the caller can see, or within one folder. */
export const driveSearch = DeclareMethod<
  { query: string, folderId?: string, limit?: number },
  DriveSearchResult
>('driveSearch');
/** Asks for a file to be read by OCR — the one extraction that is never automatic. */
export const driveRequestOcr = DeclareMethod<{ _id: string }>('driveRequestOcr');
export const driveFolderChildren = DeclareMethod<{ parentId?: string }, DriveFolderRow[]>('driveFolderChildren');
/** Who and what a permission can be granted to, which is also how a stored principal gets a label. */
export const drivePrincipalOptions = DeclareMethod<void, DrivePrincipalOption[]>('drivePrincipalOptions');
/** A folder's own permissions, plus the ones it inherits, for the permissions panel. */
export const driveFolderPermissions = DeclareMethod<
  { _id: string },
  { acl: DriveAclEntry[], ownAcl?: DriveAclEntry[], path: string, access: DriveAccessLevel }
>('driveFolderPermissions');
export const driveFileDetail = DeclareMethod<{ _id: string }, DriveFileDetail>('driveFileDetail');
/** Short-lived download/preview urls for files the caller may read, keyed by file id. */
export const driveFileUrls = DeclareMethod<
  { ids: string[], versionId?: string, download?: boolean, rendition?: 'pdf' },
  { [fileId: string]: string }
>('driveFileUrls');
/** A spreadsheet or csv as rows, read on the server so no workbook library ships to the browser. */
export const drivePreviewSheet = DeclareMethod<{ _id: string, sheet?: string }, DriveSheetPreview>('drivePreviewSheet');
/** A page of the pictures inside an archive. The drive's own door to it; Project Management has another. */
export const driveGallery = DeclareMethod<
  { _id: string, versionId?: string, from?: number, limit?: number },
  DriveGalleryPage
>('driveGallery');
/**
 * Indexes an archive again — after the thumbnail width changes, or a failed run. The archive itself is
 * untouched, so this only ever rebuilds derived data.
 */
export const driveReindexArchive = DeclareMethod<{ _id: string }>('driveReindexArchive');
/** A text file's contents, for reading and for the editor. */
export const driveTextContent = DeclareMethod<{ _id: string }, DriveTextContent>('driveTextContent');
/** Saves edited text as a NEW version, so an edit inherits the version history like any other change. */
export const driveSaveText = DeclareMethod<{ _id: string, text: string, changeNote?: string }, string>('driveSaveText');
/** Zips everything the caller may read under a folder, and returns a one-shot download url. */
export const driveZipFolder = DeclareMethod<{ folderId: string }, DriveZipResult>('driveZipFolder');

export const driveCreateFolder = DeclareMethod<{ parentId?: string, name: string }, string>('driveCreateFolder');
export const driveRenameFolder = DeclareMethod<{ _id: string, name: string }>('driveRenameFolder');
export const driveMoveFolder = DeclareMethod<{ _id: string, parentId?: string }>('driveMoveFolder');
export const driveTrashFolder = DeclareMethod<{ _id: string, trashed: boolean }>('driveTrashFolder');
export const driveDeleteFolder = DeclareMethod<{ _id: string }, { folders: number, files: number }>('driveDeleteFolder');

export const driveCreateFile = DeclareMethod<
  { folderId: string, file: FileMeta, name?: string, changeNote?: string },
  string
>('driveCreateFile');
export const driveAddFileVersion = DeclareMethod<
  { driveFileId: string, file: FileMeta, changeNote?: string },
  string
>('driveAddFileVersion');
export const driveRevertVersion = DeclareMethod<{ driveFileId: string, versionId: string }, string>('driveRevertVersion');
export const driveRenameFile = DeclareMethod<{ _id: string, name: string }>('driveRenameFile');
export const driveMoveFile = DeclareMethod<{ _id: string, folderId: string }>('driveMoveFile');
export const driveTrashFile = DeclareMethod<{ _id: string, trashed: boolean }>('driveTrashFile');
export const driveDeleteFile = DeclareMethod<{ _id: string }>('driveDeleteFile');
export const driveStarFile = DeclareMethod<{ _id: string, starred: boolean }>('driveStarFile');

/** Sets or clears a node's own permissions. Clearing it returns the node to inheriting its parent's. */
export const driveSetAcl = DeclareMethod<
  { kind: 'folder' | 'file', _id: string, acl?: DriveAclEntry[] },
  { folders: number, files: number }
>('driveSetAcl');

export const driveCreateShare = DeclareMethod<
  { target: { kind: 'folder' | 'file', id: string }, label?: string, expiresInDays?: number, withCode?: boolean, requireEmail?: boolean },
  { _id: string, url: string, code?: string }
>('driveCreateShare');
export const driveListShares = DeclareMethod<{ target?: { kind: 'folder' | 'file', id: string } }, DriveShareRow[]>('driveListShares');
export const driveRevokeShare = DeclareMethod<{ _id: string }>('driveRevokeShare');
export const driveShareVisits = DeclareMethod<{ _id: string }, DriveShareVisitRow[]>('driveShareVisits');
/**
 * The public reader. Takes no session at all — the jwt names a share and the row decides the rest, which
 * is what makes a link revocable. Returns a challenge instead of content when a code or an email is owed.
 */
export const driveReadShare = DeclareMethod<
  { jwt: string, code?: string, email?: string, folderId?: string },
  { challenge?: DriveShareChallenge, payload?: DriveSharePayload }
>('driveReadShare');

export interface DriveStorageCheck {
  storage: DriveStorageKind;
  wrote: boolean;
  readBack: boolean;
  removed: boolean;
  bytes: number;
  ms: number;
  error?: string;
}

/**
 * Round-trips one tiny object through a storage driver — write, read back, delete — and reports each
 * step. This is what makes a bucket believable before anything is pointed at it, and the only thing that
 * turns the S3 driver from written into verified.
 */
export const driveStorageCheck = DeclareMethod<{ storage?: DriveStorageKind }, DriveStorageCheck>('driveStorageCheck');

/**
 * Files a batch of a staged directory into the drive, from a manifest saying where each file belongs.
 * Super users only, batched, idempotent, and a dry run says what it would do.
 */
export const driveImportFiles = DeclareMethod<
  { root: string, manifest: string, limit?: number, dryRun?: boolean, orgId?: string },
  { filed: number, skipped: number, missing: number, failed: number, remaining: number }
>('driveImportFiles');

/**
 * Brings Project Management documents filed before the drive existed into it, pointing at the bytes
 * where they already are. Super users only, and the dry run is the point: it says what it would do.
 */
export const driveAdoptPmDocuments = DeclareMethod<
  { dryRun?: boolean, limit?: number, orgId?: string },
  { documents: number, versions: number, missing: number, remaining: number }
>('driveAdoptPmDocuments');

/** Rebuilds materialized permissions from authored ACLs. Idempotent; for the dev tool and after a repair. */
export const driveRebuildPermissions = DeclareMethod<{ orgId?: string }, { folders: number, files: number }>('driveRebuildPermissions');

Meteor.isServer && Meteor.startup(async () => {
  await DriveFolders.createIndexAsync({ orgId: 1, parentId: 1, name: 1 });
  await DriveFolders.createIndexAsync({ orgId: 1, ancestorIds: 1 });
  await DriveFolders.createIndexAsync({ orgId: 1, path: 1 });
  await DriveFolders.createIndexAsync({ readers: 1 });
  await DriveFolders.createIndexAsync({ 'ref.collection': 1, 'ref.id': 1 });
  await DriveFiles.createIndexAsync({ orgId: 1, folderId: 1, trashedAt: 1 });
  await DriveFiles.createIndexAsync({ orgId: 1, ancestorIds: 1 });
  await DriveFiles.createIndexAsync({ orgId: 1, md5: 1 });
  await DriveFiles.createIndexAsync({ 'ref.collection': 1, 'ref.id': 1 });
  await DriveFiles.createIndexAsync({ orgId: 1, 'source.kind': 1, 'source.ref': 1 });
  await DriveFiles.createIndexAsync({ readers: 1 });
  await DriveFiles.createIndexAsync({ starredBy: 1 });
  await DriveFileVersions.createIndexAsync({ driveFileId: 1, versionNumber: -1 });
  await DriveFileVersions.createIndexAsync({ 'blob.url': 1 });
  await DriveEvents.createIndexAsync({ 'ref.id': 1, at: -1 });
  await DriveEvents.createIndexAsync({ orgId: 1, at: -1 });
  await DriveShares.createIndexAsync({ orgId: 1, 'target.id': 1 });
  await DriveShareVisits.createIndexAsync({ shareId: 1, at: -1 });
  await DriveArchiveEntries.createIndexAsync({ archiveMd5: 1, index: 1 });
  await DriveArchiveEntries.createIndexAsync({ archiveMd5: 1, path: 1 }, { unique: true });
  await DriveArchiveEntries.createIndexAsync({ 'thumb.url': 1 });
  await DriveArchiveEntries.createIndexAsync({ driveFileId: 1 });
  await DriveFileTexts.createIndexAsync({ versionId: 1 }, { unique: true });
  await DriveFileTexts.createIndexAsync({ driveFileId: 1 });
});
